/**
 * Commission eligibility — Lab top-level `commission-report` /
 * BigQuery `commisionDashboard/*` → CM-API `POST /v1/account/bqEligibleUsers`
 * (+ bqIneligibleUsers, bqGetEventWiseBetCount).
 */

import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'

/** Lab / CM-API page size for bqEligibleUsers / bqIneligibleUsers. */
export const COMMISSION_ELIGIBILITY_PAGE_SIZE = 100

/** Lab controller rejects ranges longer than 8 days. */
export const COMMISSION_MAX_RANGE_DAYS = 8

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export type CommissionEligibilityTab = 'eligible' | 'ineligible'

export type CommissionEligibilityQuery = {
  /** Hierarchy filters (UUIDs) — Lab optional `tripleMaster`…`master`. */
  tripleMaster?: string
  doubleMaster?: string
  superMaster?: string
  master?: string
  /** Optional member UUIDs (`player` on CM-API). */
  memberIds: string[]
  from: string
  to: string
  tab: CommissionEligibilityTab
  page: number
  pageSize: number
}

/** Lab `getEventWiseBetCount` row — events drawer table. */
export type CommissionEventBetRow = {
  id: string
  eventName: string
  date: string
  bets: number
}

export type CommissionEligibilityRow = {
  id: string
  playerName: string
  masterName: string
  superMasterName: string
  doubleMasterName: string
  tripleMasterName: string
  totalEventsPlayed: number
  totalBetsPlayed: number
  /** Demo / fallback; live drawer uses `fetchCommissionEventBets`. */
  events: CommissionEventBetRow[]
}

export type CommissionEligibilityResult = {
  unavailable: boolean
  reason?: string
  asOf: string
  rows: CommissionEligibilityRow[]
  total: number
  page: number
  pageSize: number
}

export type CommissionEligibilityListResponse = {
  status: number
  message: string
  data: {
    totalCount: number
    data: Array<{
      PlayerId?: string
      PlayerName?: string
      MasterName?: string
      SuperMaster?: string
      DoubleMaster?: string
      TripleMaster?: string
      CompanyMaster?: string
      TotalEventsPlayed?: number
      TotalBetsPlayed?: number
    }>
  }
}

export type CommissionEventBetResponse = {
  status: number
  message: string
  data: Array<{
    PlayerId?: string
    PlayerName?: string
    EventName?: string
    Date?: string
    BetsPlayed?: number
  }>
}

/** Lab `getChildUser` / CM-API `bqGetChildUser` row — member chip picker. */
export type CommissionChildUser = {
  id: string
  username: string
}

export type CommissionChildUserResponse = {
  status: number
  message: string
  data: Array<{
    _id?: string
    username?: string
    status?: string
    accessLevel?: string
  }>
}

export type CommissionChildUserQuery = {
  player: string
  tripleMaster?: string
  doubleMaster?: string
  superMaster?: string
  master?: string
}

const DEMO_ROWS: CommissionEligibilityRow[] = [
  {
    id: 'elig-1',
    playerName: 'ravi_07',
    masterName: 'm_north',
    superMasterName: 'sm_delhi',
    doubleMasterName: 'dm_west',
    tripleMasterName: 'tm_ops',
    totalEventsPlayed: 4,
    totalBetsPlayed: 18,
    events: [
      { id: 'ev-1', eventName: 'IND v AUS — Match Odds', date: '2026-08-12', bets: 6 },
      { id: 'ev-2', eventName: 'MI v CSK — Fancy', date: '2026-08-13', bets: 7 },
      { id: 'ev-3', eventName: 'ENG v NZ — Bookmaker', date: '2026-08-14', bets: 5 },
    ],
  },
  {
    id: 'elig-2',
    playerName: 'neha_22',
    masterName: 'm_south',
    superMasterName: 'sm_chennai',
    doubleMasterName: 'dm_west',
    tripleMasterName: 'tm_ops',
    totalEventsPlayed: 2,
    totalBetsPlayed: 9,
    events: [
      { id: 'ev-4', eventName: 'KKR v RCB — Fancy', date: '2026-08-11', bets: 5 },
      { id: 'ev-5', eventName: 'IND v SA — Match Odds', date: '2026-08-15', bets: 4 },
    ],
  },
  {
    id: 'inelig-1',
    playerName: 'amit_x',
    masterName: 'm_north',
    superMasterName: 'sm_delhi',
    doubleMasterName: 'dm_west',
    tripleMasterName: 'tm_ops',
    totalEventsPlayed: 1,
    totalBetsPlayed: 2,
    events: [
      { id: 'ev-6', eventName: 'Demo market — low volume', date: '2026-08-10', bets: 2 },
    ],
  },
]

function s(v: unknown): string {
  return v == null ? '' : String(v).trim()
}

function n(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (v && typeof v === 'object' && 'value' in (v as object)) {
    return n((v as { value: unknown }).value)
  }
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

export function isCommissionMemberUuid(raw: string): boolean {
  return UUID_RE.test(raw.trim())
}

export function rangeDayCount(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.POSITIVE_INFINITY
  return Math.round(Math.abs(b - a) / 86_400_000)
}

/** Body for CM-API bqEligibleUsers / bqIneligibleUsers. */
export function commissionEligibilityRequestBody(
  query: CommissionEligibilityQuery,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    fromDate: query.from,
    toDate: query.to,
    page: Math.max(1, query.page || 1),
  }
  if (query.tripleMaster && isCommissionMemberUuid(query.tripleMaster)) {
    body.tripleMaster = query.tripleMaster.trim()
  }
  if (query.doubleMaster && isCommissionMemberUuid(query.doubleMaster)) {
    body.doubleMaster = query.doubleMaster.trim()
  }
  if (query.superMaster && isCommissionMemberUuid(query.superMaster)) {
    body.superMaster = query.superMaster.trim()
  }
  if (query.master && isCommissionMemberUuid(query.master)) {
    body.master = query.master.trim()
  }
  const players = query.memberIds
    .map((id) => id.trim())
    .filter((id) => isCommissionMemberUuid(id))
  if (players.length) body.player = players
  return body
}

export function mapCommissionEligibilityList(
  response: CommissionEligibilityListResponse,
  query: CommissionEligibilityQuery,
): CommissionEligibilityResult {
  if (response.status !== 200) {
    return unavailableCommissionEligibility(
      query,
      response.message || 'Commission eligibility request failed.',
    )
  }
  const raw = response.data?.data ?? []
  const rows: CommissionEligibilityRow[] = raw.map((r, i) => {
    const id = s(r.PlayerId) || `row-${i}`
    return {
      id,
      playerName: s(r.PlayerName) || '—',
      masterName: s(r.MasterName) || '—',
      superMasterName: s(r.SuperMaster) || '—',
      doubleMasterName: s(r.DoubleMaster) || '—',
      tripleMasterName: s(r.TripleMaster) || '—',
      totalEventsPlayed: n(r.TotalEventsPlayed),
      totalBetsPlayed: n(r.TotalBetsPlayed),
      events: [],
    }
  })
  return {
    unavailable: false,
    asOf: new Date().toISOString(),
    rows,
    total: n(response.data?.totalCount) || rows.length,
    page: query.page,
    pageSize: COMMISSION_ELIGIBILITY_PAGE_SIZE,
  }
}

export function mapCommissionEventBets(
  response: CommissionEventBetResponse,
): CommissionEventBetRow[] {
  if (response.status !== 200) return []
  return (response.data ?? []).map((r, i) => {
    const date = s(r.Date)
    const eventName = s(r.EventName) || '—'
    return {
      id: `${date || 'd'}-${eventName}-${i}`,
      eventName,
      date,
      bets: n(r.BetsPlayed),
    }
  })
}

export async function fetchCommissionEligibility(
  query: CommissionEligibilityQuery,
): Promise<CommissionEligibilityResult> {
  if (!query.from || !query.to) {
    return unavailableCommissionEligibility(query, 'Choose a week to search.')
  }
  if (rangeDayCount(query.from, query.to) > COMMISSION_MAX_RANGE_DAYS) {
    return unavailableCommissionEligibility(
      query,
      `Date range cannot be greater than ${COMMISSION_MAX_RANGE_DAYS} days (Lab commission-report).`,
    )
  }
  const nonUuidMembers = query.memberIds
    .map((id) => id.trim())
    .filter((id) => id && !isCommissionMemberUuid(id))
  if (nonUuidMembers.length) {
    return unavailableCommissionEligibility(
      query,
      'Member filter expects a member UUID (Lab player id). Clear the field or paste a UUID.',
    )
  }

  const body = commissionEligibilityRequestBody(query)
  const response =
    query.tab === 'ineligible'
      ? await cmApi.bqIneligibleUsers(authHeaders(), body)
      : await cmApi.bqEligibleUsers(authHeaders(), body)
  return mapCommissionEligibilityList(response, query)
}

export async function fetchCommissionEventBets(args: {
  from: string
  to: string
  userIds: string[]
  isEligible: boolean
}): Promise<CommissionEventBetRow[]> {
  if (!args.from || !args.to || args.userIds.length === 0) return []
  const response = await cmApi.bqGetEventWiseBetCount(authHeaders(), {
    fromDate: args.from,
    toDate: args.to,
    userIds: args.userIds,
    isEligible: args.isEligible,
  })
  return mapCommissionEventBets(response)
}

export function mapCommissionChildUsers(
  response: CommissionChildUserResponse,
): CommissionChildUser[] {
  if (response.status !== 200) return []
  return (response.data ?? [])
    .map((r) => ({
      id: s(r._id),
      username: s(r.username),
    }))
    .filter((r) => r.id && r.username)
}

/** Lab getChildUser — BQ player username search under TM→M. */
export async function fetchCommissionChildUsers(
  query: CommissionChildUserQuery,
): Promise<CommissionChildUser[]> {
  const body: Record<string, unknown> = { player: query.player ?? '' }
  if (query.tripleMaster) body.tripleMaster = query.tripleMaster
  if (query.doubleMaster) body.doubleMaster = query.doubleMaster
  if (query.superMaster) body.superMaster = query.superMaster
  if (query.master) body.master = query.master
  const response = await cmApi.bqGetChildUser(authHeaders(), body)
  return mapCommissionChildUsers(response)
}

export function demoCommissionEligibility(
  query: CommissionEligibilityQuery,
): CommissionEligibilityResult {
  const pool =
    query.tab === 'eligible'
      ? DEMO_ROWS.filter((r) => r.id.startsWith('elig'))
      : DEMO_ROWS.filter((r) => r.id.startsWith('inelig'))
  const filtered =
    query.memberIds.length === 0
      ? pool
      : pool.filter((r) =>
          query.memberIds.some(
            (id) =>
              r.playerName.toLowerCase().includes(id.toLowerCase()) || r.id === id,
          ),
        )
  const start = (query.page - 1) * query.pageSize
  return {
    unavailable: false,
    asOf: new Date().toISOString(),
    rows: filtered.slice(start, start + query.pageSize),
    total: filtered.length,
    page: query.page,
    pageSize: query.pageSize,
  }
}

export function unavailableCommissionEligibility(
  query: CommissionEligibilityQuery,
  reason = 'Commission eligibility is temporarily unavailable.',
): CommissionEligibilityResult {
  return {
    unavailable: true,
    reason,
    asOf: new Date().toISOString(),
    rows: [],
    total: 0,
    page: query.page,
    pageSize: query.pageSize,
  }
}

/** Previous ISO week (Mon–Sun), ending last Sunday — Lab default. */
export function defaultEligibilityWeek(today = new Date()): { from: string; to: string } {
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
  const day = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() - day)
  const to = d.toISOString().slice(0, 10)
  const fromDate = new Date(d)
  fromDate.setUTCDate(fromDate.getUTCDate() - 6)
  const from = fromDate.toISOString().slice(0, 10)
  return { from, to }
}

/** Shift an ISO week (Mon–Sun) by ±7 days. */
export function shiftEligibilityWeek(
  from: string,
  to: string,
  dir: -1 | 1,
): { from: string; to: string } {
  const start = Date.parse(`${from}T00:00:00Z`) + dir * 7 * 86_400_000
  const end = Date.parse(`${to}T00:00:00Z`) + dir * 7 * 86_400_000
  return {
    from: new Date(start).toISOString().slice(0, 10),
    to: new Date(end).toISOString().slice(0, 10),
  }
}
